import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Loader2, UploadCloud, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet, ArrowLeft } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Jobs Import modal (Uleadz CSV/XLSX).
 *
 * 3 steps:
 *   1. upload    — user picks file → POST /preview
 *   2. review    — show parsed rows, duplicates, new-sources/types, per-row skip
 *   3. done      — summary of created jobs + errors
 *
 * Existing database schema is NOT altered — this modal only sends rows through
 * the existing /api/jobs endpoint via the /jobs-import/execute wrapper.
 */
export default function ImportJobsModal({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("upload"); // upload | review | done | progress
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // preview response
  const [rowDecisions, setRowDecisions] = useState({}); // index -> { skip }
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);    // { batch_id, status, processed, total, ... }
  const [pollingId, setPollingId] = useState(null);  // setInterval handle

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setRowDecisions({});
    setResult(null);
    setProgress(null);
    if (pollingId) { clearInterval(pollingId); setPollingId(null); }
  };

  const close = () => {
    onOpenChange(false);
    // Delay reset so the dialog animates out first
    setTimeout(reset, 250);
  };

  const token = () => localStorage.getItem("token");

  const authedAxios = () =>
    axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token()}` },
    });

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authedAxios().post("/jobs-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Default: auto-skip duplicates; skip error rows (can't be imported anyway)
      // Iterate ALL rows (not just the 20-row UI preview) so decisions apply to every submittable row.
      const init = {};
      (data.all_rows || data.preview || []).forEach((r) => {
        init[r.index] = { skip: Boolean(r.duplicate) || (r.errors && r.errors.length > 0) };
      });
      setRowDecisions(init);
      setStep("review");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const toggleSkip = (idx) => {
    setRowDecisions((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), skip: !(prev[idx]?.skip) },
    }));
  };

  const skipAllDuplicates = () => {
    const next = { ...rowDecisions };
    (preview?.all_rows || preview?.preview || []).forEach((r) => {
      if (r.duplicate) next[r.index] = { skip: true };
    });
    setRowDecisions(next);
  };

  const importableCount = () => {
    const rowsSrc = preview?.all_rows || preview?.preview || [];
    return rowsSrc.filter(
      (r) => !(rowDecisions[r.index]?.skip) && (!r.errors || r.errors.length === 0)
    ).length;
  };

  const handleExecute = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      // Submit ALL validated rows (not just the 20 shown in the preview grid)
      const source = preview.all_rows || preview.preview || [];
      const rows = source
        .filter((r) => !r.errors || r.errors.length === 0)
        .map((r) => ({
          payload: r.payload,
          skip: Boolean(rowDecisions[r.index]?.skip),
          pending_source_name: r.pending_source_name || null,
          pending_type_name: r.pending_type_name || null,
          raw: r.raw || null,
        }));
      const { data } = await authedAxios().post("/jobs-import/execute", {
        batch_name: file?.name ? `Uleadz: ${file.name}` : "Uleadz import",
        rows,
      });

      // ── Background-mode response ─────────────────────
      if (data.status === "queued") {
        setProgress({
          batch_id: data.batch_id,
          status: "queued",
          total: data.total,
          processed_count: 0,
          success_count: 0,
          failed_count: 0,
          skipped_count: 0,
          progress_pct: 0,
          notify_email: data.notify_email,
        });
        setStep("progress");
        toast.success(`Import queued — we'll email you at ${data.notify_email || "your account address"} when all ${data.total} rows are done.`);
        startPolling(data.batch_id);
        return;
      }

      // ── Sync-mode response ───────────────────────────
      setResult(data);
      setStep("done");
      if (typeof onImported === "function") onImported(data);
      toast.success(`Imported ${data.success_count} jobs`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import failed";
      toast.error(String(msg));
    } finally {
      setImporting(false);
    }
  };

  const startPolling = (batchId) => {
    const intervalId = setInterval(async () => {
      try {
        const { data } = await authedAxios().get(`/jobs-import/batches/${batchId}`);
        setProgress(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(intervalId);
          setPollingId(null);
          if (typeof onImported === "function") onImported(data);
          if (data.status === "completed") {
            toast.success(`Background import complete: ${data.success_count}/${data.total_rows_submitted} jobs imported`);
          } else {
            toast.error(`Background import failed: ${data.error || "unknown error"}`);
          }
        }
      } catch { /* keep polling on transient errors */ }
    }, 3000);
    setPollingId(intervalId);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="import-jobs-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import Jobs from Uleadz
          </DialogTitle>
          <DialogDescription>
            Upload a Uleadz export (CSV or XLSX). Your existing jobs, customers, and schema
            are not modified — each valid row becomes a new job using your normal job flow.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <UploadStep file={file} setFile={setFile} />
        )}

        {step === "review" && preview && (
          <ReviewStep
            preview={preview}
            rowDecisions={rowDecisions}
            toggleSkip={toggleSkip}
            skipAllDuplicates={skipAllDuplicates}
          />
        )}

        {step === "done" && result && (
          <DoneStep result={result} />
        )}

        {step === "progress" && progress && (
          <ProgressStep progress={progress} />
        )}

        <DialogFooter className="mt-2 flex-wrap gap-2">
          {step === "upload" && (
            <>
              <Button variant="outline" onClick={close} data-testid="import-cancel-btn">Cancel</Button>
              <Button
                className="btn-primary"
                onClick={handleUpload}
                disabled={!file || uploading}
                data-testid="import-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                Parse File
              </Button>
            </>
          )}
          {step === "review" && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep("upload")}
                data-testid="import-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={handleExecute}
                disabled={importing || importableCount() === 0}
                data-testid="import-execute-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {importableCount() > (preview?.sync_import_threshold || 100)
                  ? `Queue ${importableCount()} Job${importableCount() === 1 ? "" : "s"} (background)`
                  : `Import ${importableCount()} Job${importableCount() === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
          {step === "progress" && (
            <>
              <Button variant="outline" onClick={close} data-testid="import-close-progress-btn">
                Close — keep running in background
              </Button>
              {(progress?.status === "completed" || progress?.status === "failed") && (
                <Button className="btn-primary" onClick={close} data-testid="import-done-btn">Done</Button>
              )}
            </>
          )}
          {step === "done" && (
            <Button className="btn-primary" onClick={close} data-testid="import-done-btn">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Step components
// ──────────────────────────────────────────────────────────────────────────────

function UploadStep({ file, setFile }) {
  const onFileChange = (e) => setFile(e.target.files?.[0] || null);
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };
  return (
    <div className="space-y-4 overflow-auto" data-testid="import-upload-step">
      <label
        htmlFor="uleadz-file"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-muted/30 transition-colors"
      >
        <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <div className="font-medium text-foreground">
          {file ? file.name : "Drop your Uleadz export here or click to browse"}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Accepts .xlsx, .csv, .tsv up to 10 MB
        </div>
        <input
          id="uleadz-file"
          type="file"
          accept=".xlsx,.csv,.tsv"
          className="hidden"
          onChange={onFileChange}
          data-testid="import-file-input"
        />
      </label>
      <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border">
        <div className="font-medium text-foreground mb-1">What happens on parse:</div>
        <ul className="list-disc ml-5 space-y-0.5">
          <li>We detect the column mapping (Ticket, Client, Phone, Status, etc.)</li>
          <li>Each row is validated and shown in a preview</li>
          <li>Duplicate ticket numbers and rows matching existing jobs are flagged</li>
          <li>Missing Job Sources and Job Types will be auto-created (Technicians won't)</li>
          <li><strong>Up to 7,500 jobs per import.</strong> Files over 100 rows run in the background — we'll email you when they're done.</li>
          <li><strong>Nothing is imported until you click "Import" on the next step.</strong></li>
        </ul>
      </div>
    </div>
  );
}

function ReviewStep({ preview, rowDecisions, toggleSkip, skipAllDuplicates }) {
  return (
    <div className="flex-1 overflow-auto space-y-3 pr-1" data-testid="import-review-step">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Summary label="Total" value={preview.total_rows} />
        <Summary label="Valid" value={preview.valid_count} tone="ok" />
        <Summary label="Warnings" value={preview.warning_count} tone="warn" />
        <Summary label="Errors" value={preview.error_count} tone="err" />
        <Summary label="Duplicates" value={preview.duplicate_count} tone="warn" />
      </div>

      {/* Background-mode banner (>100 valid rows) */}
      {preview.valid_count > (preview.sync_import_threshold || 100) && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 text-sm" data-testid="import-async-banner">
          <div className="flex items-start gap-2">
            <UploadCloud className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-foreground">Large import — will run in the background</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                This import has more than {preview.sync_import_threshold || 100} rows, so we'll queue it and process in chunks.
                You'll get an email when it's done. You can safely close this dialog after clicking import.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Auto-create notice */}
      {(preview.new_sources_to_create?.length > 0 || preview.new_types_to_create?.length > 0) && (
        <div className="text-xs rounded-lg border border-border bg-muted/30 p-3 space-y-1" data-testid="import-new-items-notice">
          {preview.new_sources_to_create?.length > 0 && (
            <div>
              <span className="font-medium text-foreground">New Job Sources to create: </span>
              <span className="text-muted-foreground">{preview.new_sources_to_create.join(", ")}</span>
            </div>
          )}
          {preview.new_types_to_create?.length > 0 && (
            <div>
              <span className="font-medium text-foreground">New Job Types to create: </span>
              <span className="text-muted-foreground">{preview.new_types_to_create.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      {/* Preview note */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          {preview.total_rows > preview.preview.length ? (
            <>Previewing first {preview.preview.length} of <strong className="text-foreground">{preview.total_rows}</strong> rows. <strong className="text-foreground">All {preview.valid_count} valid rows</strong> will be imported.</>
          ) : (
            <>All {preview.total_rows} rows shown. {preview.valid_count} will be imported.</>
          )}
        </div>
        {preview.duplicate_count > 0 && (
          <Button size="sm" variant="outline" onClick={skipAllDuplicates} data-testid="import-skip-dupes-btn">
            Skip all duplicates
          </Button>
        )}
      </div>

      {/* Row table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left w-10">Skip</th>
              <th className="px-2 py-2 text-left">Ticket</th>
              <th className="px-2 py-2 text-left">Customer</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Scheduled</th>
              <th className="px-2 py-2 text-left">Source / Type</th>
              <th className="px-2 py-2 text-left">Tech</th>
              <th className="px-2 py-2 text-left">Financials</th>
              <th className="px-2 py-2 text-left">Issues</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {preview.preview.map((r) => {
              const hasErrors = r.errors && r.errors.length > 0;
              const skipped = rowDecisions[r.index]?.skip;
              return (
                <tr
                  key={r.index}
                  className={skipped ? "opacity-50" : ""}
                  data-testid={`import-row-${r.index}`}
                >
                  <td className="px-2 py-2">
                    <Checkbox
                      checked={Boolean(skipped)}
                      disabled={hasErrors}
                      onCheckedChange={() => toggleSkip(r.index)}
                      data-testid={`import-row-skip-${r.index}`}
                    />
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">{r.payload?.ticket_number || "—"}</td>
                  <td className="px-2 py-2">
                    <div className="font-medium text-foreground">{r.payload?.customer_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{r.payload?.customer_phone || ""}</div>
                  </td>
                  <td className="px-2 py-2"><StatusBadge status={r.payload?.status} /></td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">
                    {r.payload?.scheduled_date ? new Date(r.payload.scheduled_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    <div>{r.pending_source_name ? <span className="text-amber-600">+ {r.pending_source_name}</span> : (r.raw?.job_source || "—")}</div>
                    <div>{r.pending_type_name ? <span className="text-amber-600">+ {r.pending_type_name}</span> : (r.raw?.job_type || "—")}</div>
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {r.payload?.assigned_to ? (
                      <span className="text-emerald-600 dark:text-emerald-400">✓ matched</span>
                    ) : r.raw?.technician ? (
                      <div>
                        <div className="text-amber-600 dark:text-amber-400">unmatched</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={r.raw.technician}>
                          {(r.raw.technician || "").replace(/\s+/g, " ").trim().slice(0, 20)}
                        </div>
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {(r.payload?.total_charged || r.payload?.parts || r.payload?.payments) ? (
                      <div className="space-y-0.5">
                        {r.payload?.total_charged ? (
                          <div className="font-semibold text-emerald-600 dark:text-emerald-400">${Number(r.payload.total_charged).toFixed(2)}</div>
                        ) : null}
                        {r.payload?.parts?.length ? (
                          <div className="text-[10px] text-muted-foreground">parts ${Number(r.payload.parts.reduce((s, p) => s + Number(p.cost || 0), 0)).toFixed(2)}</div>
                        ) : null}
                        {r.payload?.payments?.length ? (
                          <div className="text-[10px] text-muted-foreground">paid ${Number(r.payload.payments.reduce((s, p) => s + Number(p.amount || 0), 0)).toFixed(2)}</div>
                        ) : null}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {hasErrors && (
                      <div className="flex items-start gap-1 text-red-600">
                        <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{r.errors.join("; ")}</span>
                      </div>
                    )}
                    {r.duplicate && (
                      <div className="flex items-start gap-1 text-amber-600">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>Duplicate: {r.duplicate.reason}</span>
                      </div>
                    )}
                    {!hasErrors && r.warnings?.length > 0 && (
                      <div className="flex items-start gap-1 text-muted-foreground">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{r.warnings.join("; ")}</span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProgressStep({ progress }) {
  const pct = progress.progress_pct || 0;
  const done = progress.status === "completed" || progress.status === "failed";
  const total = progress.total_rows_submitted || progress.total || 0;
  return (
    <div className="space-y-4 overflow-auto" data-testid="import-progress-step">
      <div className={`rounded-lg border p-4 ${done && progress.status === "completed" ? "border-green-500/30 bg-green-500/5" : done ? "border-red-500/30 bg-red-500/5" : "border-blue-500/30 bg-blue-500/5"}`}>
        <div className="flex items-center gap-2 font-medium">
          {done && progress.status === "completed" ? (
            <><CheckCircle2 className="w-5 h-5 text-green-500" /><span className="text-green-700 dark:text-green-400">Import complete</span></>
          ) : done ? (
            <><XCircle className="w-5 h-5 text-red-500" /><span className="text-red-700 dark:text-red-400">Import failed</span></>
          ) : (
            <><Loader2 className="w-5 h-5 text-blue-500 animate-spin" /><span className="text-blue-700 dark:text-blue-400">Running in background…</span></>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          {done ? (
            <>Finished processing {total} row{total === 1 ? "" : "s"}.</>
          ) : (
            <>You can close this window — we'll email you at <strong className="text-foreground">{progress.notify_email || "your account address"}</strong> when it's done.</>
          )}
        </p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>{progress.processed_count || 0} of {total} rows processed</span>
          <span className="font-semibold text-foreground">{pct}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${done && progress.status === "completed" ? "bg-green-500" : done ? "bg-red-500" : "bg-blue-500"}`}
            style={{ width: `${pct}%` }}
            data-testid="import-progress-bar"
          />
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-2">
        <Summary label="Imported" value={progress.success_count || 0} tone="ok" />
        <Summary label="Failed" value={progress.failed_count || 0} tone="err" />
        <Summary label="Skipped" value={progress.skipped_count || 0} tone="warn" />
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Batch ID: <code className="text-foreground">{progress.batch_id}</code>
      </p>
    </div>
  );
}


function DoneStep({ result }) {
  return (
    <div className="space-y-3 overflow-auto" data-testid="import-done-step">
      <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4">
        <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
          <CheckCircle2 className="w-5 h-5" /> Import complete
        </div>
        <div className="text-sm text-muted-foreground mt-1" data-testid="import-result-summary">
          {result.success_count} created · {result.failed_count} failed · {result.skipped_count} skipped
        </div>
      </div>

      {(result.created_sources?.length > 0 || result.created_types?.length > 0) && (
        <div className="text-xs rounded-lg border border-border bg-muted/30 p-3 space-y-1">
          {result.created_sources?.length > 0 && (
            <div>
              <span className="font-medium text-foreground">Created Job Sources: </span>
              <span className="text-muted-foreground">{result.created_sources.join(", ")}</span>
            </div>
          )}
          {result.created_types?.length > 0 && (
            <div>
              <span className="font-medium text-foreground">Created Job Types: </span>
              <span className="text-muted-foreground">{result.created_types.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      {result.failed?.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm">
          <div className="font-medium text-red-700 dark:text-red-400 mb-1">
            {result.failed.length} row{result.failed.length === 1 ? "" : "s"} failed
          </div>
          <ul className="list-disc ml-5 space-y-0.5 text-xs text-muted-foreground max-h-40 overflow-auto">
            {result.failed.slice(0, 20).map((f, i) => (
              <li key={i}>
                Row {f.index + 1}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Small helpers
// ──────────────────────────────────────────────────────────────────────────────

function Summary({ label, value, tone }) {
  const toneClass =
    tone === "ok" ? "text-green-600" :
    tone === "warn" ? "text-amber-600" :
    tone === "err" ? "text-red-600" : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
    scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    completed_pending_payment: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  const cls = map[status] || "bg-gray-100 text-gray-700";
  return <Badge className={`${cls} border-0 capitalize`}>{(status || "—").replace(/_/g, " ")}</Badge>;
}
